import { memo, useState, useCallback, useMemo, useEffect } from 'react'
import ScaledText from '@/components/ScaledText/ScaledText'
import PopverButton from '@/components/PopverButton/PopverButton'
import PayCode from '@/components/PayCode/PayCode'
// import double11 from '@/assets/images/double11.png'
import './PaidItem.less'

// 类型定义
interface PaidItemType {
  label: string
  desc: string[]
  key: number
  origin: number
  price: number
  unit: string
}

interface PriceSummary {
  origin: number
  price: number
  diff: number
  items: any[]
}

// 常量数据
const PAID_ITEMS: PaidItemType[] = [
  {
    label: '开题报告',
    desc: ['· 知网重复率高于15% 包退款', '· 支持无限AI改稿', '· 图表、公式、代码免费', '· 6060万知网中英文真实文献'],
    key: 1,
    origin: 69,
    price: 25.9,
    unit: '篇'
  },
  {
    label: '文献综述',
    desc: ['· 知网重复率高于15% 包退款', '· 支持无限AI改稿', '· 图表、公式、代码免费', '· 6060万知网中英文真实文献'],
    key: 2,
    origin: 69,
    price: 25.9,
    unit: '篇'
  },
  {
    label: '毕业论文',
    desc: ['· 知网重复率高于15% 包退款', '· 支持无限AI改稿', '· 图表、公式、代码免费', '· 6060万知网中英文真实文献'],
    key: 3,
    origin: 129,
    price: 99,
    unit: '篇'
  },
  {
    label: '课程论文',
    desc: ['· 知网重复率高于15% 包退款', '· 支持无限AI改稿', '· 图表、公式、代码免费', '· 6060万知网中英文真实文献'],
    key: 4,
    origin: 69,
    price: 49,
    unit: '篇'
  },
  {
    label: '排版格式',
    desc: ['· 全国高校通用标准模板', '· 支持按学校要求定制化排版', '· 一分钟快速排版', '· 支持无限次格式微调'],
    key: 8,
    origin: 59,
    price: 29,
    unit: '篇'
  },
  {
    label: '答辩PPT',
    desc: ['· 5000+主题&布局免费使用', '· 3种方式AI自动生成大纲', '· 支持无限次修改主题&布局', '· 支持Office、WPS二次编辑'],
    key: 5,
    origin: 59,
    price: 29,
    unit: '份'
  },
  {
    label: '降重',
    desc: ['· 支持知网、维普、万方', '\u3000重复率高于15%包退款', '· 支持逐段降重 超高性价比', '· 支持中英文论文'],
    key: 6,
    origin: 3.9,
    price: 1.9,
    unit: '千字'
  },
  {
    label: '降AIGC',
    desc: ['· 支持知网、维普、万方', '\u3000AIGC率高于25%包退款', '· 支持逐段降重 超高性价比', '· 支持中英文论文'],
    key: 7,
    origin: 5.9,
    price: 2.9,
    unit: '千字'
  }
]

// const DOUBLE11_LABELS = new Set(['开题报告', '文献综述'])
const calculatePrice = (p1: number, p2: number, type: string) => {
  return type == 'plus' ? (p1 * 10000 + p2 * 10000) / 10000 : (p1 * 10000 - p2 * 10000) / 10000
}
const PaidItem = ({ type, quantities }: any) => {
  const [visible, setVisible] = useState(false)
  const [actives, setActives] = useState<number[]>(type || [])

  // 初始化数量，支持从外部传入
  const initialTypeCount = useMemo(() => {
    const defaultCounts = {
      1: 1,
      2: 1,
      3: 1,
      4: 1,
      8: 1,
      5: 1,
      7: 1,
      6: 1
    }

    // 如果传入了 quantities，则使用传入的数量
    if (quantities && typeof quantities === 'object') {
      return { ...defaultCounts, ...quantities }
    }

    return defaultCounts
  }, [quantities])

  const [typeCount, setTypeCount] = useState<Record<string, number>>(initialTypeCount)

  // 同步外部传入的数量变化
  useEffect(() => {
    if (quantities && typeof quantities === 'object') {
      setTypeCount(prev => ({ ...prev, ...quantities }))
    }
  }, [quantities])

  // 处理数量增减
  const handleQuantityChange = useCallback(
    (e: React.MouseEvent, count: number, key: string | number) => {
      if (!actives.includes(+key)) {
        return
      }
      e.stopPropagation()
      setTypeCount(prev => {
        const newCount = prev[key] + count
        // 确保数量不小于1
        if (newCount < 1) return prev
        return { ...prev, [key]: newCount }
      })
    },
    [actives]
  )

  // 处理选中状态切换
  const handleItemToggle = useCallback((key: number) => {
    setActives(prev => (prev.includes(key) ? prev.filter(item => item != key) : [...prev, key]))
  }, [])

  // 处理支付
  const handlePay = useCallback(() => {
    if (actives.length === 0) {
      console.warn('请选择付费项')
      return
    }
    setVisible(true)
  }, [actives, typeCount])

  // 计算价格汇总 - 使用 useMemo 优化性能
  const priceSummary = useMemo((): PriceSummary => {
    let totalOrigin = 0
    let totalPrice = 0
    const items: any = []
    actives?.forEach((key: any) => {
      const item = PAID_ITEMS.find(item => item.key == key)
      if (item) {
        const quantity = typeCount[key] || 1
        totalOrigin = calculatePrice(totalOrigin, (item.origin * 10000 * quantity) / 10000, 'plus')
        totalPrice = calculatePrice(totalPrice, (item.price * 10000 * quantity) / 10000, 'plus')
        items.push({ id: item.key, count: quantity })
      }
    })

    return { origin: totalOrigin, price: totalPrice, items, diff: calculatePrice(totalOrigin, totalPrice, 'reduce') }
  }, [actives, typeCount])
  return (
    <>
      <ul className='PaidItem-container'>
        {PAID_ITEMS.map((item: any) => {
          const isActive = actives.includes(item.key)
          const quantity = typeCount[item.key] || 1
          // const showDouble11Badge = DOUBLE11_LABELS.has(item.label)
          return (
            <li key={item.key} className={`${isActive ? 'active' : ''}`} onClick={() => handleItemToggle(item.key)}>
              <div className='paidItem-title'>
                <span>{item.label}</span>
                <div className='paidItem-icon'>
                  <i
                    className={`icon-jian ${quantity <= 1 ? 'disabled' : ''}`}
                    onClick={e => handleQuantityChange(e, -1, item.key)}
                  />
                  <label>{quantity}</label>
                  <i className='icon-jia1' onClick={e => handleQuantityChange(e, 1, item.key)} />
                </div>
              </div>
              <div className={`price`}>
                <span className='originPrice'>￥{item.origin}</span>
                <span className='mo'>¥</span>
                <span className='nowPrice'>{item.price}</span>
                <span className='unit'>/{item.unit}</span>
                {/* {showDouble11Badge ? <img className='double11' src={double11} /> : null} */}
              </div>
              <div className='paidItem-desc'>
                {item.desc.map((desc: string, idx: number) => (
                  <ScaledText key={idx} size={10}>
                    {desc}
                  </ScaledText>
                ))}
              </div>
            </li>
          )
        })}
        <li className='double11_2' />
        <li className='last'>
          <div className='left'>
            <span>立省 ¥{priceSummary.diff}</span>
            <span>原价 ¥{priceSummary.origin}</span>
          </div>
          <div className='totalPrice'>
            ¥<span>{priceSummary.price}</span>
          </div>
          <PopverButton
            type='primary'
            onClick={handlePay}
            text='立即下单'
            content='请选择付费项'
            isGradient={false}
            disabled={actives.length === 0}
            className={`${actives.length ? 'pay-btn-active pay-btn' : 'pay-btn'}`}
          />
        </li>
      </ul>
      {visible && (
        <PayCode
          visible={visible}
          onCancel={() => {
            // onCancel?.()
            setVisible(false)
          }}
          price={priceSummary.price}
          diff={priceSummary.diff}
          originalPrice={priceSummary.origin}
          items={priceSummary.items}
        />
      )}
    </>
  )
}

export default memo(PaidItem)
